'use client'

// What is coming up in his town.
//
// The calendar has been load-bearing for a while (posters, anticipation,
// missed-event regret) but only he could see it, plus Sam through the
// admin page. Now that events can come from the town as well as from Sam,
// the noticeboard goes public: people should be able to see what is
// coming, plan to be there watching, and see which events THEY chose.
//
// It reads as a calendar rather than a feed: one full-width strip you page
// through two at a time, each card wearing a dot that says whether it is
// happening now or still ahead. Stacked sections made a short schedule
// look like an empty page, and a long one look like a list of unrelated
// things.
//
// Sourced live from the sim calendar. Entries the town voted in carry a
// "chosen by the town" pill; gifts will carry their own when they exist.

import { useCallback, useEffect, useState } from 'react'
import useGameStore from '../../stores/gameStore'
import { useAccount, authFetch } from '../auth/useUser'
import { Drawer, Card, Pill, Meter } from './Drawer'
import { Tabs } from './Tabs'
import { kawaii } from '../../lib/ui/theme'
import { BIRD_NAME } from '../../lib/world/identity'
import { resolveTier, calendarPowers, CALENDAR_POWERS } from '../../lib/tiers'
import { VOTE_MIN_ACCEPTED } from '../../lib/ballots'
import { previewAccepted, demoRequested, RANK_PREVIEW_ON, SAMPLE_BALLOT } from '../../lib/ui/rankPreview'
import Link from 'next/link'

const POLL_MS = 60_000
const GAP = 14

// Measured against the cream card, not picked by eye: the obvious greens
// all land under 4.5:1 on #f7e7d2 at this weight.
const ON_NOW = '#2a6e31'
const AHEAD = '#5f42ab'

// The same nouns he uses, so the drawer and his diary agree on names.
const PLACE = {
  museum: 'the museum', cinema: 'the cinema', rave: 'the rave', bar: 'the bar',
  chinese: 'the chinese takeaway', junk: 'the burger place', bakery: 'the bakery',
  caffeine: 'the coffee shop',
}
const KIND = {
  exhibition: { label: 'exhibition', color: '#8fb8e8' },
  film: { label: 'film', color: '#c7a6e8' },
  gig: { label: 'gig', color: '#f0b064' },
  special: { label: 'special', color: '#a8d6a0' },
}

function when(e, now) {
  const day = 24 * 3600 * 1000
  const fmt = (ts) => {
    const d = new Date(ts)
    const dayName = d.toLocaleDateString(undefined, { weekday: 'long' })
    const time = d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })
    if (ts - now < day && new Date(ts).getDate() === new Date(now).getDate()) return `today ${time}`
    if (ts - now < 2 * day) return `${dayName} ${time}`
    return `${d.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' })} ${time}`
  }
  if (e.status === 'active') return `until ${fmt(e.endsAt)}`
  return fmt(e.startsAt)
}

// Entries have arrived with an empty blurb (the admin form only insisted on
// one for exhibitions and films), and a bare title like "the small strong
// one" reads as broken rather than as mysterious. Say what little is known.
function blurbFor(e) {
  const written = (e.blurb || '').trim()
  if (written) return written
  const kind = KIND[e.kind]?.label || e.kind
  return `a ${kind} at ${PLACE[e.place] || `the ${e.place}`}. no word yet on what it is.`
}

export function CalendarDrawer() {
  const open = useGameStore((s) => s.calendarOpen)
  const setOpen = useGameStore((s) => s.setCalendarOpen)
  const setCanPoster = useGameStore((s) => s.setCanPoster)
  const [events, setEvents] = useState(null)
  const [ballots, setBallots] = useState([])
  const [mine, setMine] = useState({})
  const [role, setRole] = useState(null)
  // Seeded rather than left at zero: the create tab dates its preview from
  // this, and it must be a real clock before the first fetch lands.
  const [now, setNow] = useState(() => Date.now())
  // Which tab you are on, once you have chosen one. Null until then, so
  // the opening can be decided from what is actually waiting rather than
  // hardcoded to What's on. Derived below, never set from an effect.
  const [picked, setPicked] = useState(null)
  const [blocked, setBlocked] = useState(null)
  // Read once on the first client render. The drawer never renders on the
  // server (it is closed until something opens it), so there is no snapshot
  // to mismatch and no need to bounce these through an effect.
  const [forced] = useState(previewAccepted)
  const [demo] = useState(demoRequested)
  // Set by whoever opened the drawer: the menu knows whether the calendar
  // dot was lit, and that dot only ever means an unseen ballot.
  const toVote = useGameStore((s) => s.calendarToVote)
  const markSeen = useGameStore((s) => s.markSeen)
  const lastBallotAt = useGameStore((s) => s.noticeState.lastBallotAt)

  const load = useCallback(() => {
    fetch('/api/calendar', { cache: 'no-store' })
      .then((r) => r.json())
      .then((b) => { if (Array.isArray(b.events)) { setEvents(b.events); setNow(Date.now()) } })
      .catch(() => {})
    // authFetch so the response can say which option is already yours;
    // signed out it degrades to the public read. This poll is also what
    // closes due ballots (the route sweeps on read).
    authFetch('/api/ballots', { cache: 'no-store' })
      .then((r) => r.json())
      .then((b) => {
        if (Array.isArray(b.ballots)) setBallots(b.ballots)
        if (b.mine) setMine(b.mine)
      })
      .catch(() => {})
    authFetch('/api/role', { cache: 'no-store' })
      .then((r) => r.json())
      .then((b) => {
        if (b && typeof b.accepted === 'number') {
          setRole(b)
          setCanPoster(b.accepted >= CALENDAR_POWERS.find((p) => p.id === 'propose').min)
        }
      })
      .catch(() => {})
  }, [setCanPoster])

  useEffect(() => {
    if (!open) return
    load()
    const id = setInterval(load, POLL_MS)
    return () => clearInterval(id)
  }, [open, load])

  // Having it open IS looking, so anything that arrives while you are here
  // counts as seen. Keyed on the timestamp as well as on open: a ballot
  // that opens while you are reading should not light a dot behind it.
  useEffect(() => {
    if (open) markSeen('calendar')
  }, [open, lastBallotAt, markSeen])

  if (!open) return null

  // Now first, then soonest. One strip, so the order has to carry what the
  // headings used to.
  const scheduled = (events || [])
    .filter((e) => e.status === 'active' || e.status === 'upcoming')
    .sort((a, b) => {
      if ((a.status === 'active') !== (b.status === 'active')) return a.status === 'active' ? -1 : 1
      return a.startsAt - b.startsAt
    })
  const real = ballots.filter((b) => b.status === 'open')
  const openBallots = real.length || !demo ? real : [SAMPLE_BALLOT]

  const accepted = forced ?? role?.accepted ?? 0
  // Land on the vote when that is what the ping was about and you are
  // allowed to cast one. Below the rung it stays here on What's on, which
  // is where the ladder is: the gold dot on the locked tab is what says
  // something is happening, and clicking it says what it would take.
  const tab = picked ?? (toVote && accepted >= VOTE_MIN_ACCEPTED && openBallots.length ? 'vote' : 'whatson')
  const proposeMin = CALENDAR_POWERS.find((p) => p.id === 'propose').min
  const shortBy = (n) => `${n - accepted} more ${n - accepted === 1 ? 'thing' : 'things'} of yours kept`
  const TABS = [
    { id: 'whatson', label: "What's on" },
    {
      id: 'vote',
      label: 'Vote',
      // 'calendar', not a colour name: NoticeDot resolves a KIND against
      // the palette and returns null for anything it does not know, so the
      // old 'gold' meant this dot had never once rendered. Blue is also
      // the right answer, being the same blue the corner lights, which is
      // how the two are supposed to agree about what is waiting.
      notice: openBallots.length ? 'calendar' : null,
      disabled: accepted < VOTE_MIN_ACCEPTED,
      disabledHint: 'he has to keep something of yours before you can vote',
    },
    {
      id: 'create',
      label: 'Create',
      disabled: accepted < proposeMin,
      disabledHint: 'he has to know you better before you can put things on',
    },
  ]

  // A locked tab that swallows the click is the dead door again, so say why
  // and send them to the ladder, which is on the first tab.
  const pick = (id, locked) => {
    if (!locked) { setPicked(id); setBlocked(null); return }
    setPicked('whatson')
    setBlocked(
      id === 'vote'
        ? `He has to keep something of yours first. ${shortBy(VOTE_MIN_ACCEPTED)} and it opens.`
        : `Putting events on comes later. ${shortBy(proposeMin)} and it opens.`,
    )
  }

  return (
    <Drawer
      header={<Tabs tabs={TABS} active={tab} onPick={pick} />}
      onClose={() => { setPicked(null); setOpen(false) }}
      height="min(80dvh, 800px)"
      bodyClass="kawaii-scroll flex-1 overflow-y-auto min-h-0 px-6 sm:px-10 pb-14 pt-1"
    >
      <div className="max-w-5xl mx-auto">
        {blocked && (
          <p style={{ color: '#9a6a1e', fontSize: 13, fontWeight: 800, marginBottom: 10 }}>{blocked}</p>
        )}

        {tab === 'whatson' && (
          <div className="grid gap-6 md:grid-cols-2 items-start">
            {/* Left: what this page is, and where you stand on the civic
                ladder. Right: the noticeboard itself, one card at a time. */}
            <div className="min-w-0">
              <p className="mb-5" style={{ color: kawaii.inkSoft, fontSize: 14, fontWeight: 600, lineHeight: 1.6 }}>
                Take a look at scheduled events coming up in {BIRD_NAME}&rsquo;s town.
                He learns about them from posters, decides for himself if he&rsquo;d
                like to go and often regrets the ones he knew he missed. If he knows
                you, you can create an event for him.
              </p>
              <StandingBand
                role={role}
                forced={forced}
                openBallots={openBallots.length}
                toBallots={() => setPicked('vote')}
              />
            </div>
            <div className="min-w-0">
              {events === null && (
                <p style={{ color: kawaii.inkSoft, fontSize: 14, fontWeight: 600, fontStyle: 'italic' }}>reading the noticeboard…</p>
              )}
              {events !== null && !scheduled.length && (
                <p style={{ color: kawaii.inkSoft, fontSize: 14.5, fontWeight: 600, fontStyle: 'italic' }}>
                  nothing on the calendar right now. quiet weeks happen to every town.
                </p>
              )}
              {scheduled.length > 0 && <EventStrip events={scheduled} now={now} />}
            </div>
          </div>
        )}

        {tab === 'vote' && (
          <VoteTab
            ballots={openBallots}
            mine={mine}
            now={now}
            accepted={accepted}
            onVoted={load}
          />
        )}

        {tab === 'create' && (
          <CreateTab accepted={accepted} now={now} openBallots={real} onSent={load} toVote={() => setPicked('vote')} />
        )}

      </div>
    </Drawer>
  )
}

/**
 * The town's open votes.
 *
 * Its empty state does more work than most: for nearly all of the drawer's
 * life there will be nothing to decide, and "no ballots" on its own reads
 * as broken rather than as quiet.
 */
function VoteTab({ ballots, mine, now, accepted, onVoted }) {
  const canVote = accepted >= VOTE_MIN_ACCEPTED

  return (
    <>
      <p className="max-w-3xl mb-6" style={{ color: kawaii.inkSoft, fontSize: 14, fontWeight: 600, lineHeight: 1.6 }}>
        When the town has something to decide, it goes to a vote here. Whatever
        wins is put on the calendar for real, and {BIRD_NAME} meets it the way
        he meets everything else: on a poster, without being told you chose it.
      </p>

      {!ballots.length && (
        <Card>
          <p style={{ fontSize: 14.5, fontWeight: 800, marginBottom: 4 }}>nothing to decide right now</p>
          <p style={{ color: kawaii.inkSoft, fontSize: 13.5, fontWeight: 600, lineHeight: 1.55 }}>
            Votes open when enough people have put an event forward, or when
            the town is asked directly. This is where they appear.
          </p>
        </Card>
      )}

      {!canVote && ballots.length > 0 && (
        <p style={{ color: '#9a6a1e', fontSize: 13, fontWeight: 800, marginBottom: 12 }}>
          he has to keep something of yours first. leave him something worth keeping.
        </p>
      )}

      <div className="grid gap-4 md:grid-cols-2">
        {ballots.map((b) => (
          <BallotCard key={b.id} b={b} myOptionId={mine[b.id]} now={now} onVoted={onVoted} />
        ))}
      </div>
    </>
  )
}

// Where each kind of thing is allowed to happen. Mirrors CALENDAR_KINDS in
// the sim, which is what will reject it if these ever drift apart.
const KIND_PLACES = {
  exhibition: ['museum'],
  film: ['cinema'],
  gig: ['rave', 'bar'],
  special: ['chinese', 'junk', 'bakery', 'caffeine', 'bar'],
}
const DAYS = [
  { id: 1, label: 'tomorrow' },
  { id: 2, label: 'in 2 days' },
  { id: 3, label: 'in 3 days' },
  { id: 5, label: 'in 5 days' },
  { id: 7, label: 'in a week' },
]
const TIMES = [
  { id: 10, label: 'morning' },
  { id: 14, label: 'afternoon' },
  { id: 19, label: 'evening' },
  { id: 22, label: 'late' },
]
const RUNS_FOR = { gig: 4 * 3600e3, film: 3 * 3600e3, special: 3 * 86400e3, exhibition: 10 * 86400e3 }

/**
 * Putting something in his week.
 *
 * The form is the easy half. The hard half is that everything typed here
 * becomes a poster he reads on his own, so the description is required and
 * asked for in those terms rather than as an optional extra: a title with
 * nothing after it gives him nothing to look forward to, which is exactly
 * how the world ended up booking "the small strong one" against itself.
 *
 * Two doors, one form. Below the top rung it goes to the town for a vote;
 * at the top it goes straight up. Neither is wired to a route yet, so the
 * button says so instead of pretending.
 */
function CreateTab({ accepted, now, openBallots = [], onSent, toVote }) {
  const propose = CALENDAR_POWERS.find((p) => p.id === 'propose')
  const schedule = CALENDAR_POWERS.find((p) => p.id === 'schedule')
  const canPropose = accepted >= propose.min
  const goesStraightUp = accepted >= schedule.min

  const [kind, setKind] = useState('gig')
  const [place, setPlace] = useState('rave')
  const [title, setTitle] = useState('')
  const [blurb, setBlurb] = useState('')
  const [day, setDay] = useState(2)
  const [hour, setHour] = useState(19)
  const [sending, setSending] = useState(false)
  const [sent, setSent] = useState(null)
  const poster = useGameStore((st) => st.pickedPoster)
  const setPickedPoster = useGameStore((st) => st.setPickedPoster)
  const setPostersOpen = useGameStore((st) => st.setPostersOpen)
  const openShelf = () => setPostersOpen(true)

  // You get one at a time. The propose route has always refused a second,
  // but the form had no idea, so it stayed filled in after a submit with a
  // live button on it: you could type out a whole second event and only
  // find out on the press. `yours` comes back on the ballot list, so the
  // tab can just say what is true and put the vote one tap away.
  const waiting = openBallots.find((b) => b.yours)

  const pickKind = (k) => {
    setKind(k)
    if (!KIND_PLACES[k].includes(place)) setPlace(KIND_PLACES[k][0])
  }

  const put = async () => {
    setSending(true); setSent(null)
    try {
      const res = await authFetch('/api/calendar/propose', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          kind, place, title: title.trim(), blurb: blurb.trim(),
          startsAt, posterId: poster?.id,
        }),
      })
      const body = await res.json().catch(() => ({}))
      if (!res.ok) { setSent({ bad: true, text: body.error || 'that did not go up.' }); return }
      // It is out of your hands now, so nothing of it should still be
      // sitting in the form. The poster goes back on the shelf rather than
      // being deleted: it is yours, you just are not holding it any more.
      setTitle(''); setBlurb(''); setPickedPoster(null)
      setSent({
        bad: false,
        text: body.bypass
          ? 'it is on the calendar. he will find the poster himself.'
          : 'it is up for the vote. the town decides from here.',
      })
      onSent?.()
    } catch {
      setSent({ bad: true, text: 'that did not go up.' })
    } finally {
      setSending(false)
    }
  }

  const startsAt = (() => {
    const d = new Date(now + day * 86400e3)
    d.setHours(hour, 0, 0, 0)
    return d.getTime()
  })()

  const titleOk = title.trim().length >= 3 && title.trim().length <= 80
  const blurbOk = blurb.trim().length >= 10 && blurb.trim().length <= 500
  const ready = titleOk && blurbOk && !!poster
  const missing = !poster ? 'it needs a poster'
    : !titleOk ? 'it needs a name of at least 3 characters'
    : 'the poster line needs at least 10 characters'

  if (!canPropose) {
    const short = propose.min - accepted
    return (
      <>
        <p className="max-w-3xl mb-6" style={{ color: kawaii.inkSoft, fontSize: 14, fontWeight: 600, lineHeight: 1.6 }}>
          Events in {BIRD_NAME}&rsquo;s town can come from the people watching
          it. Put one forward and the town votes on it; win the town over often
          enough and you get to put things up yourself.
        </p>
        <Card>
          <p style={{ fontSize: 14.5, fontWeight: 800, marginBottom: 4 }}>he does not know you well enough yet</p>
          <p style={{ color: kawaii.inkSoft, fontSize: 13.5, fontWeight: 600, lineHeight: 1.55 }}>
            He has kept {accepted} {accepted === 1 ? 'thing' : 'things'} you left
            him. {short} more and you can put an event forward for the town to
            vote on. Nothing here can be bought: he decides what he keeps.
          </p>
        </Card>
      </>
    )
  }

  // Yours is already out there. Show it rather than a second empty form:
  // the answer to "can I put another one up" is no until the town has
  // finished with this one, and the useful thing to offer is the vote.
  if (waiting) {
    const mine = waiting.options.find((o) => !o.isDecline) || waiting.options[0]
    const left = waiting.closesAt - now
    return (
      <>
        <p className="max-w-3xl mb-6" style={{ color: kawaii.inkSoft, fontSize: 14, fontWeight: 600, lineHeight: 1.6 }}>
          Yours is up in front of the town. One at a time: when this one has
          been decided you can put another forward.
        </p>
        <Card>
          <div className="flex items-start" style={{ gap: 14 }}>
            {mine?.imageUrl && (
              // eslint-disable-next-line @next/next/no-img-element
              <img
                src={mine.imageUrl}
                alt=""
                style={{
                  width: 78, height: 117, objectFit: 'cover',
                  borderRadius: 10, border: kawaii.outlineThin, flex: 'none',
                }}
              />
            )}
            <div style={{ minWidth: 0 }}>
              <p style={{ fontSize: 15.5, fontWeight: 800, marginBottom: 3 }}>{mine?.title || waiting.question}</p>
              <p style={{ color: kawaii.inkSoft, fontSize: 13, fontWeight: 700, marginBottom: 8 }}>
                {PLACE[waiting.place] || `the ${waiting.place}`}
                {left > 0 ? ` \u00b7 the town has ${timeLeft(left)} to decide` : ' \u00b7 counting the votes'}
              </p>
              <button type="button" onClick={() => toVote?.()} style={linkStyle}>see how it is going</button>
            </div>
          </div>
        </Card>
      </>
    )
  }

  return (
    <>
      <p className="max-w-3xl mb-6" style={{ color: kawaii.inkSoft, fontSize: 14, fontWeight: 600, lineHeight: 1.6 }}>
        {goesStraightUp
          ? `He knows you well enough that this goes straight on the calendar. No vote, no veto: he will find it on a poster like anything else, and he is free to hate it.`
          : `Put an event forward and it joins the next thing the town votes on. If it wins it happens for real, and ${BIRD_NAME} finds it on a poster without being told whose it was.`}
      </p>

      {/* Breathing room around the form: the fields sat flush against
          the drawer edges and each other, and a form you are about to
          put in front of the whole town should not feel cramped. */}
      <div className="grid gap-6 md:grid-cols-2" style={{ paddingBottom: 28, paddingTop: 4 }}>
        <div className="flex flex-col" style={{ gap: 18 }}>
          <Field label="what sort of thing is it?">
            <PillRow
              options={Object.keys(KIND_PLACES).map((k) => ({ id: k, label: KIND[k]?.label || k }))}
              value={kind}
              onPick={pickKind}
            />
          </Field>

          {KIND_PLACES[kind].length > 1 && (
            <Field label="where?">
              <PillRow
                options={KIND_PLACES[kind].map((p) => ({ id: p, label: PLACE[p] || p }))}
                value={place}
                onPick={setPlace}
              />
            </Field>
          )}

          <Field label="what is it called?" note={`${title.trim().length}/80`}>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value.slice(0, 80))}
              placeholder="Bin Day"
              style={inputStyle}
            />
          </Field>

          <Field
            label="what does the poster say?"
            note={`${blurb.trim().length}/500`}
            hint="he reads this off a wall and decides for himself whether to go"
          >
            <textarea
              value={blurb}
              onChange={(e) => setBlurb(e.target.value.slice(0, 500))}
              rows={3}
              placeholder="three of them hitting things that are not instruments"
              style={{ ...inputStyle, resize: 'vertical', lineHeight: 1.5 }}
            />
          </Field>

          <Field label="when?">
            <div className="flex flex-col" style={{ gap: 8 }}>
              <PillRow options={DAYS} value={day} onPick={setDay} />
              <PillRow options={TIMES} value={hour} onPick={setHour} />
            </div>
          </Field>

          <Field
            label="the poster"
            note={poster ? 'ready' : 'required'}
            hint={`this is the actual thing that goes up on a wall in his town, and ${BIRD_NAME} looks at it before he decides anything`}
          >
            {poster ? (
              <div className="flex items-center" style={{ gap: 12 }}>
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                  src={poster.imageUrl}
                  alt=""
                  style={{
                    width: 64, height: 96, objectFit: 'cover',
                    borderRadius: 10, border: kawaii.outlineThin, flex: 'none',
                  }}
                />
                <div style={{ minWidth: 0 }}>
                  <p style={{ fontSize: 13, fontWeight: 800 }}>{poster.title || 'untitled'}</p>
                  <button type="button" onClick={openShelf} style={linkStyle}>choose a different one</button>
                </div>
              </div>
            ) : (
              <div className="flex flex-wrap items-center" style={{ gap: 8 }}>
                <button
                  type="button"
                  onClick={openShelf}
                  style={{
                    background: kawaii.creamDeep, border: kawaii.outlineThin, borderRadius: 999,
                    boxShadow: kawaii.dropSm, color: kawaii.ink,
                    padding: '6px 15px', fontSize: 13, fontWeight: 800, cursor: 'pointer',
                  }}
                >
                  choose from your posters
                </button>
                <Link href="/poster" style={linkStyle}>or make a new one</Link>
              </div>
            )}
          </Field>
        </div>

        <div>
          <p style={{ color: kawaii.inkSoft, fontSize: 12.5, fontWeight: 800, marginBottom: 8 }}>
            how it will look on the calendar
          </p>
          <EventCard
            e={{
              id: 'preview',
              kind,
              place,
              title: title.trim() || 'your event',
              blurb: blurb.trim(),
              posterUrl: poster?.imageUrl || null,
              posterFormat: poster?.format || 'poster',
              startsAt,
              endsAt: startsAt + (RUNS_FOR[kind] || 4 * 3600e3),
              status: 'upcoming',
              source: goesStraightUp ? 'gift' : 'ballot',
            }}
            now={now}
          />

          <button
            type="button"
            onClick={put}
            disabled={!ready || sending}
            style={{
              marginTop: 14, width: '100%',
              background: ready ? '#e8c04a' : kawaii.creamDeep,
              border: kawaii.outlineThin, borderRadius: 999, boxShadow: kawaii.dropSm,
              color: kawaii.ink, padding: '9px 18px', fontSize: 14, fontWeight: 800,
              cursor: ready && !sending ? 'pointer' : 'default', opacity: ready ? 1 : 0.6,
            }}
          >
            {sending ? 'putting it up…' : goesStraightUp ? 'Put it on the calendar' : 'Put it forward'}
          </button>
          <p style={{ marginTop: 8, color: kawaii.inkSoft, fontSize: 12, fontWeight: 700, lineHeight: 1.5 }}>
            {goesStraightUp
              ? 'He knows you well enough that this skips the vote. The poster goes up on the wall and he finds it himself.'
              : 'The vote opens straight away and runs about a day. If the town says yes it goes on the calendar and the poster goes up on the wall.'}
          </p>
          {sent && (
            <p style={{ marginTop: 6, color: sent.bad ? '#a8483e' : '#2a6e31', fontSize: 12.5, fontWeight: 800, lineHeight: 1.5 }}>
              {sent.text}
            </p>
          )}
          {!ready && (
            <p style={{ marginTop: 6, color: kawaii.inkSoft, fontSize: 12, fontWeight: 700, lineHeight: 1.5 }}>
              {missing}
            </p>
          )}
        </div>
      </div>

    </>
  )
}

const linkStyle = {
  background: 'none', border: 'none', padding: 0, color: '#5f42ab',
  fontSize: 12.5, fontWeight: 800, textDecoration: 'underline', cursor: 'pointer',
}

const inputStyle = {
  width: '100%',
  background: kawaii.cream,
  border: kawaii.outlineThin,
  borderRadius: 12,
  color: kawaii.ink,
  padding: '8px 12px',
  fontSize: 14,
  fontWeight: 700,
  fontFamily: 'inherit',
  outline: 'none',
}

function Field({ label, note, hint, children }) {
  return (
    <label className="block">
      <div className="flex items-baseline justify-between gap-3" style={{ marginBottom: 5 }}>
        <span style={{ fontSize: 13, fontWeight: 800, color: kawaii.ink }}>{label}</span>
        {note && <span style={{ fontSize: 11.5, fontWeight: 700, color: kawaii.inkSoft }}>{note}</span>}
      </div>
      {children}
      {hint && (
        <p style={{ marginTop: 4, color: kawaii.inkSoft, fontSize: 11.5, fontWeight: 600, lineHeight: 1.45 }}>{hint}</p>
      )}
    </label>
  )
}

function PillRow({ options, value, onPick }) {
  return (
    <div className="flex flex-wrap" style={{ gap: 6 }}>
      {options.map((o) => {
        const on = o.id === value
        return (
          <button
            key={o.id}
            type="button"
            onClick={() => onPick(o.id)}
            style={{
              background: on ? kawaii.ink : kawaii.cream,
              color: on ? kawaii.cream : kawaii.inkSoft,
              border: kawaii.outlineThin,
              borderRadius: 999,
              padding: '4px 13px',
              fontSize: 12.5,
              fontWeight: 800,
              cursor: on ? 'default' : 'pointer',
            }}
          >
            {o.label}
          </button>
        )
      })}
    </div>
  )
}

// Two cards on a desktop, one on a phone. Subscribed rather than measured
// in an effect so a rotation repaints in the same pass as the resize.



/**
 * The schedule itself: full width, two at a time, paged.
 *
 * Paging by whole containers rather than by card keeps the arrows honest
 * at both widths (one press moves two cards on desktop, one on a phone)
 * and means the strip never rests showing half of anything.
 */
function EventStrip({ events, now }) {
  const [page, setPage] = useState(0)
  const [touch, setTouch] = useState(null)
  // One card per slide: the strip lives in the right-hand column now,
  // beside the ladder, and a single card at a time is the whole point.
  const perPage = 1

  // Clamped on read, not in an effect: perPage changes under a rotation and
  // the list is refetched while you sit here, so either can shorten the
  // strip beneath a page you are already on.
  const pages = Math.max(1, Math.ceil(events.length / perPage))
  const at = Math.min(Math.max(page, 0), pages - 1)
  const first = at * perPage + 1
  const last = Math.min(events.length, (at + 1) * perPage)

  const go = (d) => setPage(Math.min(Math.max(at + d, 0), pages - 1))

  const slides = []
  for (let i = 0; i < events.length; i += perPage) slides.push(events.slice(i, i + perPage))

  return (
    <div>
      <div
        style={{ overflow: 'hidden' }}
        onTouchStart={(e) => setTouch(e.touches[0]?.clientX ?? null)}
        onTouchEnd={(e) => {
          if (touch == null) return
          const dx = (e.changedTouches[0]?.clientX ?? touch) - touch
          setTouch(null)
          if (Math.abs(dx) > 44) go(dx < 0 ? 1 : -1)
        }}
      >
        <div
          className="flex items-stretch"
          style={{
            gap: GAP,
            transform: `translateX(calc(${-at * 100}% - ${at * GAP}px))`,
            transition: 'transform 320ms cubic-bezier(0.22, 1, 0.36, 1)',
          }}
        >
          {slides.map((chunk, si) => (
            <div key={si} style={{ flex: '0 0 100%' }}>
              {chunk.map((e) => (
                <EventCard key={e.id} e={e} now={now} />
              ))}
            </div>
          ))}
        </div>
      </div>

      {pages > 1 && (
        <div className="flex items-center gap-1.5" style={{ marginTop: 10 }}>
          {[-1, 1].map((d) => {
            const dead = d < 0 ? at === 0 : at === pages - 1
            return (
              <button
                key={d}
                type="button"
                onClick={() => go(d)}
                disabled={dead}
                aria-label={d < 0 ? 'earlier' : 'later'}
                style={{
                  width: 26, height: 26, borderRadius: 999,
                  background: kawaii.cream, border: kawaii.outlineThin,
                  color: kawaii.ink, fontSize: 13, fontWeight: 800,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  cursor: dead ? 'default' : 'pointer',
                  opacity: dead ? 0.35 : 1,
                }}
              >
                {d < 0 ? '‹' : '›'}
              </button>
            )
          })}
          <span style={{ color: kawaii.inkSoft, fontSize: 12, fontWeight: 700, marginLeft: 4 }}>
            {first === last ? first : `${first}-${last}`} of {events.length}
          </span>
        </div>
      )}
    </div>
  )
}

/** Green for happening, purple for ahead. The dot carries it, the word confirms it. */
function StatusDot({ active }) {
  const color = active ? ON_NOW : AHEAD
  return (
    <span className="inline-flex items-center" style={{ gap: 6 }}>
      <span
        style={{
          width: 9, height: 9, borderRadius: 999, background: color,
          boxShadow: `0 0 0 2.5px ${active ? 'rgba(42, 110, 49, 0.2)' : 'rgba(95, 66, 171, 0.18)'}`,
          flex: 'none',
        }}
      />
      <span style={{ color, fontSize: 12, fontWeight: 800, letterSpacing: '0.01em' }}>
        {active ? 'on now' : 'coming up'}
      </span>
    </span>
  )
}

function EventCard({ e, now }) {
  const kind = KIND[e.kind] || { label: e.kind, color: kawaii.creamDeep }
  return (
    <Card style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <div className="flex items-center justify-between gap-3 mb-2">
        <StatusDot active={e.status === 'active'} />
        <Pill bg={kind.color}>{kind.label}</Pill>
      </div>
      {/* Picture beside the words, not stacked above them. A portrait
          poster set full width is 1.5x the card wide in height, which
          pushed the title, the place and the time out of the frame: the
          card became a picture with a caption nobody could read. */}
      <div className="flex" style={{ gap: 12 }}>
        {e.posterUrl && (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            src={e.posterUrl}
            alt=""
            style={{
              width: 92, flex: 'none',
              aspectRatio: e.posterFormat === 'billboard' ? '2 / 1' : '2 / 3',
              objectFit: 'cover',
              borderRadius: 10, border: kawaii.outlineThin,
              alignSelf: 'flex-start',
            }}
          />
        )}
        <div style={{ minWidth: 0 }}>
          <span style={{ fontSize: 16, fontWeight: 800, lineHeight: 1.35 }}>{e.title}</span>
          <p style={{ color: kawaii.inkSoft, fontSize: 13, fontWeight: 700, margin: '4px 0 8px' }}>
            {PLACE[e.place] || `the ${e.place}`} · {when(e, now)}
          </p>
          <p style={{ color: kawaii.ink, fontSize: 13.5, fontWeight: 600, lineHeight: 1.55 }}>{blurbFor(e)}</p>
        </div>
      </div>
      {/* The town's fingerprints. The whole point of ballots is that the
          result is VISIBLY yours, so the mark goes on the event itself. */}
      {e.source === 'ballot' && (
        <div style={{ marginTop: 'auto', paddingTop: 10 }}>
          <Pill bg="#e8c04a">chosen by the town</Pill>
        </div>
      )}
      {e.source === 'gift' && (
        <div style={{ marginTop: 'auto', paddingTop: 10 }}>
          <Pill bg="#e0743c">a gift from someone</Pill>
        </div>
      )}
    </Card>
  )
}

/**
 * One open vote. Tallies are live and public (polls people know work this
 * way), your pick stays pressed, and changing your mind is allowed until
 * the clock runs out. The vote gate (he has to know you) is enforced
 * server-side; here it only shapes the error copy.
 */
function BallotCard({ b, myOptionId, now, onVoted }) {
  const { signedIn } = useAccount()
  const setProfileOpen = useGameStore((s) => s.setProfileOpen)
  const setCalendarOpen = useGameStore((s) => s.setCalendarOpen)
  const [busy, setBusy] = useState(false)
  const [note, setNote] = useState(null)

  const total = b.options.reduce((n, o) => n + o.votes, 0)
  const kind = KIND[b.kind] || { label: b.kind, color: kawaii.creamDeep }
  // If any option has art then every option needs the same column, or the
  // one without (always the decline) sits a poster-width to the left of
  // the rest and the card reads as broken rather than as a choice.
  const hasArt = b.options.some((o) => o.imageUrl)
  const left = b.closesAt - now

  const cast = async (optionId) => {
    if (b.sample) { setNote('this one is a sample, its votes go nowhere.'); return }
    if (!signedIn) { setCalendarOpen(false); setProfileOpen(true); return }
    setBusy(true); setNote(null)
    try {
      const res = await authFetch('/api/ballots/vote', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ballotId: b.id, optionId }),
      })
      const body = await res.json().catch(() => ({}))
      if (!res.ok) setNote(body.error || 'that vote did not land, try again.')
      else onVoted?.()
    } catch {
      setNote('that vote did not land, try again.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Card>
      <div className="flex items-start justify-between gap-3 mb-1">
        <span style={{ fontSize: 16, fontWeight: 800, lineHeight: 1.35 }}>{b.question}</span>
        <Pill bg={b.sample ? '#a8483e' : kind.color}>{b.sample ? 'sample' : kind.label}</Pill>
      </div>
      <p style={{ color: kawaii.inkSoft, fontSize: 13, fontWeight: 700, marginBottom: 12 }}>
        {PLACE[b.place] || `the ${b.place}`}
        {b.sample ? ' · not a real vote' : ` · voting ends ${left > 0 ? `in ${timeLeft(left)}` : 'any moment'}`}
      </p>

      <div className="flex flex-col gap-3">
        {b.options.map((o) => {
          const share = total > 0 ? o.votes / total : 0
          const chosen = myOptionId === o.id
          return (
            <button
              key={o.id}
              type="button"
              disabled={busy}
              onClick={() => cast(o.id)}
              className="text-left flex w-full items-start"
              style={{ cursor: busy ? 'default' : 'pointer', gap: 12 }}
              title={chosen ? 'your vote' : 'vote for this'}
            >
              {/* The poster is the thing being voted on, so it belongs in
                  the card. Left of the text because every poster is 2:3 and
                  a portrait image across the top of a wide card either
                  towers over it or gets cropped to a stripe. */}
              {o.imageUrl ? (
                // eslint-disable-next-line @next/next/no-img-element
                <img
                  src={o.imageUrl}
                  alt=""
                  style={{
                    width: 64, height: 96, objectFit: 'cover',
                    borderRadius: 10, border: kawaii.outlineThin, flex: 'none',
                  }}
                />
              ) : hasArt ? (
                <span
                  className="flex items-center justify-center text-center"
                  style={{
                    width: 64, height: 96, flex: 'none',
                    borderRadius: 10, border: `2px dashed ${kawaii.inkSoft}40`,
                    color: kawaii.inkSoft, fontSize: 11, fontWeight: 800, lineHeight: 1.3,
                    padding: 6,
                  }}
                >
                  the wall stays bare
                </span>
              ) : null}
              <div style={{ minWidth: 0, flex: 1 }}>
                <div className="flex items-baseline justify-between gap-3 mb-1">
                  <span style={{ fontSize: 14.5, fontWeight: 800, color: kawaii.ink }}>
                    {o.title}
                    {chosen && (
                      <span style={{ marginLeft: 8, color: '#4e7a46', fontSize: 12, fontWeight: 800 }}>your vote</span>
                    )}
                  </span>
                  <span style={{ color: kawaii.inkSoft, fontSize: 12.5, fontWeight: 700, whiteSpace: 'nowrap' }}>
                    {o.votes} {o.votes === 1 ? 'vote' : 'votes'}
                  </span>
                </div>
                <Meter pct={share} color={chosen ? '#7fbf72' : '#f0b064'} height={10} />
                {o.blurb && (
                  <p style={{ marginTop: 5, color: kawaii.inkSoft, fontSize: 12.5, fontWeight: 600, lineHeight: 1.5 }}>{o.blurb}</p>
                )}
              </div>
            </button>
          )
        })}
      </div>

      <p style={{ marginTop: 12, color: kawaii.inkSoft, fontSize: 12.5, fontWeight: 700 }}>
        {signedIn
          ? 'tap to vote. you can change your mind until it closes.'
          : 'connect to vote. it costs nothing.'}
      </p>
      {note && (
        <p style={{ marginTop: 6, color: '#a8483e', fontSize: 12.5, fontWeight: 700 }}>{note}</p>
      )}
    </Card>
  )
}

function timeLeft(ms) {
  const h = Math.floor(ms / 3600000)
  if (h >= 48) return `${Math.round(h / 24)} days`
  if (h >= 1) return `${h}h ${Math.floor((ms % 3600000) / 60000)}m`
  return `${Math.max(1, Math.floor(ms / 60000))}m`
}

/**
 * How much of the town you are trusted with, and what is still above you.
 *
 * The ladder is worth showing even at the bottom rung: the whole point of
 * standing being earned is that people can see what earning it gets them.
 */
function StandingBand({ role, forced, openBallots, toBallots }) {
  const { signedIn } = useAccount()
  const setProfileOpen = useGameStore((s) => s.setProfileOpen)
  const setCalendarOpen = useGameStore((s) => s.setCalendarOpen)

  const real = role?.accepted ?? 0
  const accepted = forced ?? real
  const pretending = forced !== null
  const tier = resolveTier(accepted)
  const powers = calendarPowers(accepted)
  const nextUp = powers.find((p) => !p.unlocked)

  return (
    <div>
      <Card>
        <div className="flex flex-wrap items-center gap-2" style={{ marginBottom: 12 }}>
          <span style={{ color: kawaii.inkSoft, fontSize: 13, fontWeight: 800 }}>You are:</span>
          <Pill bg={tier.color}>{tier.name}</Pill>
          <span style={{ color: kawaii.inkSoft, fontSize: 13, fontWeight: 700 }}>
            {signedIn || pretending
              ? `he has kept ${accepted} ${accepted === 1 ? 'thing' : 'things'} you left him`
              : 'connect and leave him something to start'}
          </span>
        </div>

        <div className="flex flex-col" style={{ gap: 10 }}>
          {powers.map((p) => (
            <PowerRow
              key={p.id}
              power={p}
              detail={p.id === 'vote' ? <VoteDetail openBallots={openBallots} toBallots={toBallots} /> : p.detail}
            />
          ))}
        </div>

        {nextUp && (
          <p style={{ marginTop: 12, color: kawaii.inkSoft, fontSize: 12.5, fontWeight: 700, lineHeight: 1.55 }}>
            {nextUp.min - accepted === 1
              ? 'one more thing of yours kept and you can '
              : `${nextUp.min - accepted} more things of yours kept and you can `}
            {nextUp.label}.
          </p>
        )}

        {!signedIn && !pretending && (
          <button
            type="button"
            onClick={() => { setCalendarOpen(false); setProfileOpen(true) }}
            style={{
              marginTop: 12, background: kawaii.creamDeep, border: kawaii.outlineThin,
              borderRadius: 999, boxShadow: kawaii.dropSm, color: kawaii.ink,
              padding: '6px 16px', fontSize: 13, fontWeight: 800, cursor: 'pointer',
            }}
          >
            connect
          </button>
        )}

        {pretending && (
          <p style={{ marginTop: 12, color: '#a8483e', fontSize: 12, fontWeight: 800 }}>
            preview only: showing this at {accepted} kept. your real standing is {real}.
          </p>
        )}
      </Card>

      {RANK_PREVIEW_ON && (
        <p style={{ marginTop: 8, color: kawaii.inkSoft, fontSize: 11.5, fontWeight: 700 }}>
          dev: ?rank=new | knows | familiar | own · &demo=1 stands up a sample vote
        </p>
      )}
    </div>
  )
}

/**
 * Where the voting actually is.
 *
 * "Is it in the profile?" was the first question the ladder produced, which
 * means a rung that says what you may do without saying where is only half
 * a sentence. When a ballot is open this jumps to it; when none is, it says
 * so rather than sending anyone hunting.
 */
function VoteDetail({ openBallots, toBallots }) {
  if (!openBallots) {
    return <>Voting opens on this page whenever the town has something to decide. The winning event goes up on the calendar and {BIRD_NAME} finds it on a poster</>
  }
  return (
    <>
      Cast your vote on{' '}
      <button
        type="button"
        onClick={toBallots}
        style={{
          background: 'none', border: 'none', padding: 0, font: 'inherit',
          color: AHEAD, fontWeight: 800, textDecoration: 'underline', cursor: 'pointer',
        }}
      >
        the vote tab
      </button>
      . The winning event goes up on the calendar and {BIRD_NAME} finds it on a poster
    </>
  )
}

function PowerRow({ power, detail }) {
  const live = power.unlocked && power.built
  const waiting = power.unlocked && !power.built
  const colour = live ? ON_NOW : waiting ? '#9a6a1e' : kawaii.inkSoft

  return (
    <div className="flex items-start" style={{ gap: 9 }}>
      <span
        style={{
          width: 16, height: 16, borderRadius: 999, flex: 'none', marginTop: 2,
          background: live ? ON_NOW : waiting ? '#e8c04a' : 'transparent',
          border: live || waiting ? 'none' : `2px dashed ${kawaii.inkSoft}`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
      >
        {live && (
          <svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true">
            <path d="M1.5 5.2 L4 7.6 L8.5 2.4" fill="none" stroke={kawaii.cream} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        )}
      </span>
      <div style={{ minWidth: 0 }}>
        <span style={{ color: power.unlocked ? kawaii.ink : kawaii.inkSoft, fontSize: 14, fontWeight: 800 }}>
          {power.label}
        </span>
        {waiting && (
          <span style={{ color: colour, fontSize: 11.5, fontWeight: 800, marginLeft: 8 }}>yours, not open yet</span>
        )}
        <p style={{ color: kawaii.inkSoft, fontSize: 12.5, fontWeight: 600, lineHeight: 1.5, marginTop: 1 }}>
          {detail ?? power.detail}
        </p>
      </div>
    </div>
  )
}

export default CalendarDrawer
